import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

interface Ejercicio {
  id: number;
  nombre: string;
  categoria: string;
  equipo: string;
  dificultad: string;
  descripcion: string;
  musculosPrincipales: string[];
  // Steps shown in mobile under "Como realizar el ejercicio".
  instrucciones?: string[];
  // Optional per-exercise tips; mobile can fall back to generic tips.
  consejos?: string[];
  tieneVideo: boolean;
  tieneAnimacion: boolean;
  tieneVideoSubido: boolean;
  urlVideo?: string;
  urlGif?: string;
  urlVideoSubido?: string;
  videoSubidoPath?: string;
  duracionVideoSegundos?: number | null;
}

interface Rutina {
  id: number;
  nombre: string;
  descripcion: string;
  nivel: string;
  duracion: number;
  ejercicios: number;
  usuariosAsignados: number;
  activa: boolean;
  es_plantilla?: boolean;
  plantilla_global?: boolean;
  creada_por_usuario?: boolean;
  sesiones?: Sesion[];
  usuarios_asignados?: string[]; // IDs de usuarios asignados
}

interface EjercicioEnRutina {
  ejercicioId: number;
  nombre: string;
  categoria: string;
  series: number;
  repeticiones: number;
  descanso: number;
  peso?: number;
}

interface Sesion {
  id: string;
  nombre: string;
  ejercicios: EjercicioEnRutina[];
}

// Add Usuario interface
interface Usuario {
  id: string;
  nombre: string;
  email: string;
  avatar: string;
  plan: string;
  estado: string;
  fechaAsignacion?: string;
  progreso?: number;
}

const categorias = ['Pecho', 'Espalda', 'Piernas', 'Hombros', 'Brazos', 'Core', 'Cardio'];
const equipos = ['Barra', 'Mancuernas', 'Polea', 'Máquina', 'Barra Fija', 'Sin Equipo', 'Kettlebell', 'Banda Elástica'];
const dificultades = ['Principiante', 'Intermedio', 'Avanzado'];
const musculosDisponibles = [
  'Pectoral Mayor', 'Dorsal Ancho', 'Trapecio', 'Deltoides', 'Deltoides Anterior',
  'Bíceps', 'Bíceps Braquial', 'Braquial Anterior', 'Tríceps', 'Tríceps Braquial',
  'Cuádriceps', 'Isquiotibiales', 'Glúteos', 'Pantorrillas',
  'Abdominales', 'Oblicuos', 'Espalda Baja', 'Core'
];

// Sesiones de ejemplo para rutinas existentes
const sesionesEjemplo: Record<number, Sesion[]> = {
  1: [
    {
      id: 'A',
      nombre: 'Sesión A - Tren Superior',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 3, repeticiones: 10, descanso: 60, peso: 0 },
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 3, repeticiones: 15, descanso: 45, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Tren Inferior',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 4, repeticiones: 10, descanso: 90, peso: 0 },
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 3, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 3, repeticiones: 30, descanso: 45, peso: 0 },
      ]
    }
  ],
  2: [
    {
      id: 'A',
      nombre: 'Sesión A - Push',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 7, nombre: 'Extensión de Tríceps', categoria: 'Brazos', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Pull',
      ejercicios: [
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 4, repeticiones: 6, descanso: 120, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 3, repeticiones: 12, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'C',
      nombre: 'Sesión C - Piernas',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 45, descanso: 60, peso: 0 },
      ]
    }
  ],
  3: [
    {
      id: 'A',
      nombre: 'Sesión A - Pecho y Tríceps',
      ejercicios: [
        { ejercicioId: 1, nombre: 'Press de Banca', categoria: 'Pecho', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 7, nombre: 'Extensión de Tríceps', categoria: 'Brazos', series: 4, repeticiones: 10, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'B',
      nombre: 'Sesión B - Espalda y Bíceps',
      ejercicios: [
        { ejercicioId: 3, nombre: 'Peso Muerto', categoria: 'Espalda', series: 5, repeticiones: 5, descanso: 180, peso: 0 },
        { ejercicioId: 4, nombre: 'Dominadas', categoria: 'Espalda', series: 4, repeticiones: 8, descanso: 90, peso: 0 },
        { ejercicioId: 6, nombre: 'Curl de Bíceps', categoria: 'Brazos', series: 4, repeticiones: 10, descanso: 60, peso: 0 },
      ]
    },
    {
      id: 'C',
      nombre: 'Sesión C - Piernas',
      ejercicios: [
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 5, repeticiones: 5, descanso: 180, peso: 0 },
      ]
    },
    {
      id: 'D',
      nombre: 'Sesión D - Hombros y Core',
      ejercicios: [
        { ejercicioId: 5, nombre: 'Press Militar', categoria: 'Hombros', series: 5, repeticiones: 5, descanso: 120, peso: 0 },
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 60, descanso: 60, peso: 0 },
      ]
    }
  ],
  4: [
    {
      id: 'A',
      nombre: 'Sesión Cardio HIIT',
      ejercicios: [
        { ejercicioId: 8, nombre: 'Plancha', categoria: 'Core', series: 4, repeticiones: 30, descanso: 30, peso: 0 },
        { ejercicioId: 2, nombre: 'Sentadilla', categoria: 'Piernas', series: 4, repeticiones: 20, descanso: 30, peso: 0 },
      ]
    }
  ]
};

export default function Rutinas() {
  const MAX_EXERCISE_VIDEO_SIZE_MB = 10;
  const MAX_EXERCISE_VIDEO_SIZE_BYTES = MAX_EXERCISE_VIDEO_SIZE_MB * 1024 * 1024;
  const { sucursalActiva } = useSucursal();
  const [activeTab, setActiveTab] = useState<'rutinas' | 'ejercicios' | 'plantillas' | 'personalizadas'>('rutinas');
  const [searchTerm, setSearchTerm] = useState('');
  const [rutinas, setRutinas] = useState<Rutina[]>([]);
  const [plantillas, setPlantillas] = useState<Rutina[]>([]);
  const [ejercicios, setEjercicios] = useState<Ejercicio[]>([]);
  const [loadingEjercicios, setLoadingEjercicios] = useState(false);
  const [errorEjercicios, setErrorEjercicios] = useState<string | null>(null);
  
  // Add missing loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingRutinaId, setDeletingRutinaId] = useState<number | null>(null);
  
  const [isNewExerciseModalOpen, setIsNewExerciseModalOpen] = useState(false);
  const [isNewRutinaModalOpen, setIsNewRutinaModalOpen] = useState(false);
  const [isEditRutinaModalOpen, setIsEditRutinaModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRutinaForAssign, setSelectedRutinaForAssign] = useState<Rutina | null>(null);
  const [selectedRutinaForDetail, setSelectedRutinaForDetail] = useState<Rutina | null>(null);
  const [selectedRutinaForEdit, setSelectedRutinaForEdit] = useState<Rutina | null>(null);
  const [detailActiveTab, setDetailActiveTab] = useState<'info' | 'sesiones' | 'usuarios'>('info');
  const [detailActiveSesion, setDetailActiveSesion] = useState<string>('A');

  // Estados para asignación de rutinas
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [templateGlobalScope, setTemplateGlobalScope] = useState(false);
  const [editTemplateGlobalScope, setEditTemplateGlobalScope] = useState(false);
  
  // Add usuarios state to fetch from Supabase
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  
  // Estados para editar ejercicio
  const [isEditExerciseModalOpen, setIsEditExerciseModalOpen] = useState(false);
  const [selectedExerciseForEdit, setSelectedExerciseForEdit] = useState<Ejercicio | null>(null);
  const [originalExerciseForEdit, setOriginalExerciseForEdit] = useState<Ejercicio | null>(null);
  const [newExerciseVideoFile, setNewExerciseVideoFile] = useState<File | null>(null);
  const [editExerciseVideoFile, setEditExerciseVideoFile] = useState<File | null>(null);
  
  // Estado para detalle de ejercicio
  const [isExerciseDetailModalOpen, setIsExerciseDetailModalOpen] = useState(false);
  const [selectedExerciseForDetail, setSelectedExerciseForDetail] = useState<Ejercicio | null>(null);

  // Add state for usuarios asignados in detail modal
  const [usuariosAsignadosDetail, setUsuariosAsignadosDetail] = useState<Usuario[]>([]);
  const [loadingUsuariosDetail, setLoadingUsuariosDetail] = useState(false);
  const [activeUserMenu, setActiveUserMenu] = useState<string | null>(null);
  const [usuariosAsignadosSearchTerm, setUsuariosAsignadosSearchTerm] = useState('');
  const [usuariosAsignadosPage, setUsuariosAsignadosPage] = useState(1);
  const [usuariosAsignadosEstadoFilter, setUsuariosAsignadosEstadoFilter] = useState('');
  const [usuariosAsignadosPlanFilter, setUsuariosAsignadosPlanFilter] = useState('');
  const USUARIOS_PER_PAGE = 5;
  
  const [newExercise, setNewExercise] = useState<Omit<Ejercicio, 'id'>>({
    nombre: '',
    categoria: '',
    equipo: '',
    dificultad: '',
    descripcion: '',
    musculosPrincipales: [],
    instrucciones: [],
    consejos: [],
    tieneVideo: false,
    tieneAnimacion: false,
    tieneVideoSubido: false,
    urlVideo: '',
    urlGif: '',
    urlVideoSubido: '',
    videoSubidoPath: '',
    duracionVideoSegundos: null
  });

  const sanitizeFileName = (name: string) =>
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 120);

  const isHttpUrl = (value?: string | null) => /^https?:\/\//i.test((value || '').trim());

  const validateExerciseVideoFile = (file: File): void => {
    if (file.size > MAX_EXERCISE_VIDEO_SIZE_BYTES) {
      throw new Error(`El video debe pesar máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB.`);
    }
  };

  const uploadExerciseVideo = async (file: File) => {
    if (!sucursalActiva?.gimnasio_id) {
      throw new Error('No hay gimnasio activo para subir el video.');
    }

    validateExerciseVideoFile(file);
    const safeName = sanitizeFileName(file.name || 'ejercicio.mp4');
    const path = `${sucursalActiva.gimnasio_id}/${Date.now()}_${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from('exercise-videos')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'video/mp4',
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage.from('exercise-videos').getPublicUrl(path);
    if (!data?.publicUrl) {
      throw new Error('No se pudo obtener la URL del video subido.');
    }

    return { publicUrl: data.publicUrl, path, duration: null as number | null };
  };

  const removeExerciseVideo = async (path?: string | null) => {
    if (!path) return;
    await supabase.storage.from('exercise-videos').remove([path]);
  };

  const applyExclusiveMediaSelection = <T extends Omit<Ejercicio, 'id'> | Ejercicio>(
    current: T,
    mode: 'video' | 'gif' | 'upload',
    enabled: boolean
  ): T => {
    const base = { ...current } as T;

    if (!enabled) {
      if (mode === 'video') {
        return { ...base, tieneVideo: false, urlVideo: '' } as T;
      }
      if (mode === 'gif') {
        return { ...base, tieneAnimacion: false, urlGif: '' } as T;
      }
      return {
        ...base,
        tieneVideoSubido: false,
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    if (mode === 'video') {
      return {
        ...base,
        tieneVideo: true,
        tieneAnimacion: false,
        tieneVideoSubido: false,
        urlGif: '',
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    if (mode === 'gif') {
      return {
        ...base,
        tieneAnimacion: true,
        tieneVideo: false,
        tieneVideoSubido: false,
        urlVideo: '',
        urlVideoSubido: '',
        videoSubidoPath: '',
        duracionVideoSegundos: null,
      } as T;
    }

    return {
      ...base,
      tieneVideoSubido: true,
      tieneVideo: false,
      tieneAnimacion: false,
      urlVideo: '',
      urlGif: '',
    } as T;
  };

  // Estado para nueva rutina con sesiones
  const [newRutina, setNewRutina] = useState({
    nombre: '',
    descripcion: '',
    nivel: '',
    duracion: 45,
    activa: true
  });
  const [sesiones, setSesiones] = useState<Sesion[]>([
    { id: 'A', nombre: 'Sesión A', ejercicios: [] }
  ]);
  const [sesionActiva, setSesionActiva] = useState('A');
  const [ejercicioSearchTerm, setEjercicioSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  // Estado para editar rutina
  const [editRutina, setEditRutina] = useState({
    id: 0,
    nombre: '',
    descripcion: '',
    nivel: '',
    duracion: 45,
    activa: true
  });
  const [editSesiones, setEditSesiones] = useState<Sesion[]>([]);
  const [editSesionActiva, setEditSesionActiva] = useState('A');
  const [editEjercicioSearchTerm, setEditEjercicioSearchTerm] = useState('');
  const [editCategoriaFiltro, setEditCategoriaFiltro] = useState('');

  const rutinasOficiales = rutinas.filter((rutina) => !rutina.creada_por_usuario);
  const rutinasPersonalizadas = rutinas.filter((rutina) => rutina.creada_por_usuario);

  const filteredRutinas = rutinasOficiales.filter(rutina =>
    rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRutinasPersonalizadas = rutinasPersonalizadas.filter(rutina =>
    rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPlantillas = plantillas.filter(rutina =>
    rutina.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rutina.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEjercicios = ejercicios.filter(ejercicio =>
    ejercicio.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ejercicio.categoria.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ejercicios filtrados para el modal de nueva rutina
  const ejerciciosDisponibles = ejercicios.filter(ejercicio => {
    const matchSearch = ejercicio.nombre.toLowerCase().includes(ejercicioSearchTerm.toLowerCase());
    const matchCategoria = !categoriaFiltro || ejercicio.categoria === categoriaFiltro;
    const sesionActual = sesiones.find(s => s.id === sesionActiva);
    const notAdded = !sesionActual?.ejercicios.some(e => e.ejercicioId === ejercicio.id);
    return matchSearch && matchCategoria && notAdded;
  });

  const handleOpenNewExerciseModal = () => {
    setNewExercise({
      nombre: '',
      categoria: '',
      equipo: '',
      dificultad: '',
      descripcion: '',
      musculosPrincipales: [],
      instrucciones: [],
      consejos: [],
      tieneVideo: false,
      tieneAnimacion: false,
      tieneVideoSubido: false,
      urlVideo: '',
      urlGif: '',
      urlVideoSubido: '',
      videoSubidoPath: '',
      duracionVideoSegundos: null
    });
    setNewExerciseVideoFile(null);
    setIsNewExerciseModalOpen(true);
  };

  const handleCloseNewExerciseModal = () => {
    setIsNewExerciseModalOpen(false);
    setNewExerciseVideoFile(null);
  };

  const handleOpenNewRutinaModal = (templateMode = false) => {
    setIsTemplateMode(templateMode);
    setTemplateGlobalScope(false);
    setNewRutina({
      nombre: '',
      descripcion: '',
      nivel: '',
      duracion: 45,
      activa: true
    });
    setSesiones([{ id: 'A', nombre: 'Sesión A', ejercicios: [] }]);
    setSesionActiva('A');
    setEjercicioSearchTerm('');
    setCategoriaFiltro('');
    setIsNewRutinaModalOpen(true);
  };

  const handleCloseNewRutinaModal = () => {
    setIsNewRutinaModalOpen(false);
    setIsTemplateMode(false);
    setTemplateGlobalScope(false);
  };

  const handleExportPlantillaToRutina = (plantilla: Rutina) => {
    const sesionesPlantilla = JSON.parse(
      JSON.stringify(plantilla.sesiones || sesionesEjemplo[plantilla.id] || [])
    ) as Sesion[];

    if (!sesionesPlantilla.length) {
      alert('La plantilla no tiene sesiones para exportar.');
      return;
    }

    setIsTemplateMode(false);
    setTemplateGlobalScope(false);
    setNewRutina({
      nombre: `${plantilla.nombre} (copia)`,
      descripcion: plantilla.descripcion || '',
      nivel: plantilla.nivel || 'Intermedio',
      duracion: plantilla.duracion || 45,
      activa: plantilla.activa ?? true
    });
    setSesiones(sesionesPlantilla);
    setSesionActiva(sesionesPlantilla[0]?.id || 'A');
    setEjercicioSearchTerm('');
    setCategoriaFiltro('');
    setIsNewRutinaModalOpen(true);
  };

  const handleOpenDetailModal = (rutina: Rutina) => {
    const sesionesDetalle = rutina.sesiones || sesionesEjemplo[rutina.id] || [];
    setSelectedRutinaForDetail(rutina);
    setDetailActiveTab('info');
    setDetailActiveSesion(sesionesDetalle[0]?.id || 'A');
    setIsDetailModalOpen(true);
    setUsuariosAsignadosSearchTerm('');
    setUsuariosAsignadosPage(1);
    setUsuariosAsignadosEstadoFilter('');
    setUsuariosAsignadosPlanFilter('');
    
    // Fetch usuarios asignados cuando se abre el modal de detalle
    if (rutina.usuarios_asignados && rutina.usuarios_asignados.length > 0) {
      fetchUsuariosAsignados(rutina.usuarios_asignados);
    } else {
      setUsuariosAsignadosDetail([]);
    }
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedRutinaForDetail(null);
    setUsuariosAsignadosDetail([]);
    setUsuariosAsignadosSearchTerm('');
    setUsuariosAsignadosPage(1);
    setUsuariosAsignadosEstadoFilter('');
    setUsuariosAsignadosPlanFilter('');
  };

  // Función para manejar el clic del botón principal
  const handleButtonClick = () => {
    if (activeTab === 'rutinas' || activeTab === 'personalizadas') {
      handleOpenNewRutinaModal(false);
    } else if (activeTab === 'plantillas') {
      handleOpenNewRutinaModal(true);
    } else {
      handleOpenNewExerciseModal();
    }
  };

  // Funciones para detalle de ejercicio
  const handleOpenExerciseDetailModal = (ejercicio: Ejercicio) => {
    setSelectedExerciseForDetail(ejercicio);
    setIsExerciseDetailModalOpen(true);
  };

  const handleCloseExerciseDetailModal = () => {
    setIsExerciseDetailModalOpen(false);
    setSelectedExerciseForDetail(null);
  };

  // Funciones para editar ejercicio
  const handleOpenEditExerciseModal = (ejercicio: Ejercicio) => {
    setSelectedExerciseForEdit(ejercicio);
    setOriginalExerciseForEdit(ejercicio);
    setEditExerciseVideoFile(null);
    setIsEditExerciseModalOpen(true);
  };

  const handleCloseEditExerciseModal = () => {
    setIsEditExerciseModalOpen(false);
    setSelectedExerciseForEdit(null);
    setOriginalExerciseForEdit(null);
    setEditExerciseVideoFile(null);
  };

  const handleSaveEditExercise = async () => {
    if (!selectedExerciseForEdit || !selectedExerciseForEdit.nombre || !selectedExerciseForEdit.categoria || !selectedExerciseForEdit.dificultad) {
      return;
    }

    if (!sucursalActiva?.gimnasio_id) {
      alert('No hay gimnasio activo para guardar el ejercicio');
      return;
    }

    try {
      let uploadedVideoUrl = selectedExerciseForEdit.urlVideoSubido || null;
      let uploadedVideoPath = selectedExerciseForEdit.videoSubidoPath || null;

      if (selectedExerciseForEdit.tieneVideo && !isHttpUrl(selectedExerciseForEdit.urlVideo)) {
        alert('Debes ingresar una URL valida de video (http/https).');
        return;
      }

      if (selectedExerciseForEdit.tieneAnimacion && !isHttpUrl(selectedExerciseForEdit.urlGif)) {
        alert('Debes ingresar una URL valida para el GIF (http/https).');
        return;
      }

      if (selectedExerciseForEdit.tieneVideoSubido) {
        if (editExerciseVideoFile) {
          const uploaded = await uploadExerciseVideo(editExerciseVideoFile);
          uploadedVideoUrl = uploaded.publicUrl;
          uploadedVideoPath = uploaded.path;
        } else if (!uploadedVideoUrl || !uploadedVideoPath) {
          alert(`Debes subir un video (máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB).`);
          return;
        }
      } else {
        uploadedVideoUrl = null;
        uploadedVideoPath = null;
      }

      const payload = {
        gimnasio_id: sucursalActiva.gimnasio_id,
        nombre: selectedExerciseForEdit.nombre,
        categoria: selectedExerciseForEdit.categoria,
        equipo: selectedExerciseForEdit.equipo,
        dificultad: selectedExerciseForEdit.dificultad,
        descripcion: selectedExerciseForEdit.descripcion || null,
        musculos_principales: selectedExerciseForEdit.musculosPrincipales || [],
        instrucciones: (selectedExerciseForEdit.instrucciones || []).map(String).map(s => s.trim()).filter(Boolean),
        consejos: (selectedExerciseForEdit.consejos || []).map(String).map(s => s.trim()).filter(Boolean),
        tiene_video: selectedExerciseForEdit.tieneVideo,
        tiene_animacion: selectedExerciseForEdit.tieneAnimacion,
        tiene_video_subido: selectedExerciseForEdit.tieneVideoSubido,
        url_video: selectedExerciseForEdit.tieneVideo ? (selectedExerciseForEdit.urlVideo || null) : null,
        url_gif: selectedExerciseForEdit.tieneAnimacion ? (selectedExerciseForEdit.urlGif || null) : null,
        url_video_subido: selectedExerciseForEdit.tieneVideoSubido ? uploadedVideoUrl : null,
        video_subido_path: selectedExerciseForEdit.tieneVideoSubido ? uploadedVideoPath : null,
        updated_at: new Date().toISOString(),
      };

      let { data, error } = await supabase
        .from('ejercicios')
        .update(payload)
        .eq('id', selectedExerciseForEdit.id)
        .select('*')
        .single();

      // Backward compat: if the DB hasn't been migrated yet, retry without new columns.
      if (error) {
        const msg = String((error as any)?.message || '');
        if (
          (
            msg.includes('instrucciones') ||
            msg.includes('consejos') ||
            msg.includes('tiene_video_subido') ||
            msg.includes('url_video_subido') ||
            msg.includes('video_subido_path')
          ) &&
          (msg.toLowerCase().includes('schema cache') || msg.toLowerCase().includes('could not find'))
        ) {
          const {
            instrucciones,
            consejos,
            tiene_video_subido,
            url_video_subido,
            video_subido_path,
            ...fallbackPayload
          } = payload as any;
          const retry = await supabase
            .from('ejercicios')
            .update(fallbackPayload)
            .eq('id', selectedExerciseForEdit.id)
            .select('*')
            .single();
          data = retry.data;
          error = retry.error as any;
        }
      }

      if (error) throw error;

      if (!selectedExerciseForEdit.tieneVideoSubido && originalExerciseForEdit?.videoSubidoPath) {
        await removeExerciseVideo(originalExerciseForEdit.videoSubidoPath);
      } else if (
        selectedExerciseForEdit.tieneVideoSubido &&
        editExerciseVideoFile &&
        originalExerciseForEdit?.videoSubidoPath &&
        originalExerciseForEdit.videoSubidoPath !== uploadedVideoPath
      ) {
        await removeExerciseVideo(originalExerciseForEdit.videoSubidoPath);
      }

      const updated = ejercicioFromRow(data as any);
      setEjercicios(prev => prev.map(e => (e.id === updated.id ? updated : e)));
      setIsEditExerciseModalOpen(false);
      setSelectedExerciseForEdit(null);
      setOriginalExerciseForEdit(null);
      setEditExerciseVideoFile(null);
    } catch (err: any) {
      console.error('Error actualizando ejercicio:', err);
      alert('Error al actualizar el ejercicio: ' + (err.message || ''));
    }
  };

  // Funciones para editar rutina
  const handleOpenEditRutinaModal = (rutina: Rutina) => {
    const rutinaSesiones = rutina.sesiones || sesionesEjemplo[rutina.id] || [];
    setSelectedRutinaForEdit(rutina);
    setEditTemplateGlobalScope(rutina.plantilla_global === true);
    setEditRutina({
      id: rutina.id,
      nombre: rutina.nombre,
      descripcion: rutina.descripcion,
      nivel: rutina.nivel,
      duracion: rutina.duracion,
      activa: rutina.activa
    });
    setEditSesiones(JSON.parse(JSON.stringify(rutinaSesiones)));
    setEditSesionActiva(rutinaSesiones[0]?.id || 'A');
    setEditEjercicioSearchTerm('');
    setEditCategoriaFiltro('');
    setIsEditRutinaModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleCloseEditRutinaModal = () => {
    setIsEditRutinaModalOpen(false);
    setSelectedRutinaForEdit(null);
    setEditTemplateGlobalScope(false);
  };

  const handleAddEditSesion = () => {
    const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nextLetter = letras[editSesiones.length];
    if (editSesiones.length < 26) {
      setEditSesiones(prev => [...prev, {
        id: nextLetter,
        nombre: `Sesión ${nextLetter}`,
        ejercicios: []
      }]);
    }
  };

  const handleRemoveEditSesion = (sesionId: string) => {
    if (editSesiones.length > 1) {
      setEditSesiones(prev => prev.filter(s => s.id !== sesionId));
      if (editSesionActiva === sesionId) {
        setEditSesionActiva(editSesiones[0].id);
      }
    }
  };

  const handleRenameEditSesion = (sesionId: string, nuevoNombre: string) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === sesionId ? { ...s, nombre: nuevoNombre } : s
    ));
  };

  const handleAddEjercicioToEditRutina = (ejercicio: Ejercicio) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? {
            ...s,
            ejercicios: [...s.ejercicios, {
              ejercicioId: ejercicio.id,
              nombre: ejercicio.nombre,
              categoria: ejercicio.categoria,
              series: 3,
              repeticiones: 12,
              descanso: 60,
              peso: 0
            }]
          }
        : s
    ));
  };

  const handleRemoveEjercicioFromEditRutina = (ejercicioId: number) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? { ...s, ejercicios: s.ejercicios.filter(e => e.ejercicioId !== ejercicioId) }
        : s
    ));
  };

  const handleUpdateEjercicioEnEditRutina = (ejercicioId: number, field: keyof EjercicioEnRutina, value: number) => {
    setEditSesiones(prev => prev.map(s => 
      s.id === editSesionActiva 
        ? {
            ...s,
            ejercicios: s.ejercicios.map(e => 
              e.ejercicioId === ejercicioId ? { ...e, [field]: value } : e
            )
          }
        : s
    ));
  };

  const handleMoveEditEjercicio = (index: number, direction: 'up' | 'down') => {
    const sesionActual = editSesiones.find(s => s.id === editSesionActiva);
    if (!sesionActual) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sesionActual.ejercicios.length) return;
    
    setEditSesiones(prev => prev.map(s => {
      if (s.id === editSesionActiva) {
        const newList = [...s.ejercicios];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        return { ...s, ejercicios: newList };
      }
      return s;
    }));
  };

  const handleSaveEditRutina = async () => {
    const totalEjercicios = editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0);
    if (!editRutina.nombre || !editRutina.nivel || totalEjercicios === 0) {
      return;
    }

    try {
      setError(null);

      const ejerciciosData = {
        nivel: editRutina.nivel,
        duracion: editRutina.duracion,
        sesiones: editSesiones,
      };

      const isEditingTemplate = selectedRutinaForEdit?.es_plantilla === true;

      const { error: updateError } = await supabase
        .from('rutinas')
        .update({
          nombre: editRutina.nombre,
          descripcion: editRutina.descripcion,
          ejercicios: ejerciciosData,
          dias_semana: editSesiones.map(s => s.id),
          activa: editRutina.activa,
          plantilla_global: isEditingTemplate ? editTemplateGlobalScope : false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editRutina.id);

      if (updateError) throw updateError;

      await fetchRutinas();
      setIsEditRutinaModalOpen(false);
      setSelectedRutinaForEdit(null);
      setEditTemplateGlobalScope(false);
    } catch (err: any) {
      console.error('Error actualizando rutina:', err);
      setError('Error al actualizar la rutina: ' + (err.message || ''));
      alert('Error al actualizar la rutina: ' + (err.message || ''));
    }
  };

  // Ejercicios disponibles para editar rutina
  const ejerciciosDisponiblesEdit = ejercicios.filter(ejercicio => {
    const matchSearch = ejercicio.nombre.toLowerCase().includes(editEjercicioSearchTerm.toLowerCase());
    const matchCategoria = !editCategoriaFiltro || ejercicio.categoria === editCategoriaFiltro;
    const sesionActual = editSesiones.find(s => s.id === editSesionActiva);
    const notAdded = !sesionActual?.ejercicios.some(e => e.ejercicioId === ejercicio.id);
    return matchSearch && matchCategoria && notAdded;
  });

  const editSesionActual = editSesiones.find(s => s.id === editSesionActiva);

  const handleNewExerciseMusculoToggle = (musculo: string) => {
    setNewExercise(prev => ({
      ...prev,
      musculosPrincipales: prev.musculosPrincipales.includes(musculo)
        ? prev.musculosPrincipales.filter(m => m !== musculo)
        : [...prev.musculosPrincipales, musculo]
    }));
  };

  const handleEditExerciseMusculoToggle = (musculo: string) => {
    setSelectedExerciseForEdit(prev => {
      if (!prev) return prev;
      const current = Array.isArray(prev.musculosPrincipales) ? prev.musculosPrincipales : [];
      const next = current.includes(musculo)
        ? current.filter(m => m !== musculo)
        : [...current, musculo];
      return { ...prev, musculosPrincipales: next };
    });
  };

  const handleToggleNewExerciseMedia = (mode: 'video' | 'gif' | 'upload', enabled: boolean) => {
    if (mode !== 'upload') {
      setNewExerciseVideoFile(null);
    }
    setNewExercise(prev => applyExclusiveMediaSelection(prev, mode, enabled));
  };

  const handleToggleEditExerciseMedia = (mode: 'video' | 'gif' | 'upload', enabled: boolean) => {
    if (mode !== 'upload') {
      setEditExerciseVideoFile(null);
    }
    setSelectedExerciseForEdit(prev => (prev ? applyExclusiveMediaSelection(prev, mode, enabled) : prev));
  };

  const handleNewExerciseVideoFileChange = async (file: File | null) => {
    if (!file) {
      setNewExerciseVideoFile(null);
      return;
    }
    validateExerciseVideoFile(file);
    setNewExerciseVideoFile(file);
    setNewExercise(prev => ({ ...prev, duracionVideoSegundos: null }));
  };

  const handleEditExerciseVideoFileChange = async (file: File | null) => {
    if (!file) {
      setEditExerciseVideoFile(null);
      return;
    }
    validateExerciseVideoFile(file);
    setEditExerciseVideoFile(file);
    setSelectedExerciseForEdit(prev => (prev ? { ...prev, duracionVideoSegundos: null } : prev));
  };

  const handleSaveExercise = async () => {
    if (!newExercise.nombre || !newExercise.categoria || !newExercise.dificultad) {
      return;
    }

    if (!sucursalActiva?.gimnasio_id) {
      alert('No hay gimnasio activo para guardar el ejercicio');
      return;
    }

    try {
      let uploadedVideoUrl: string | null = null;
      let uploadedVideoPath: string | null = null;

      if (newExercise.tieneVideo && !isHttpUrl(newExercise.urlVideo)) {
        alert('Debes ingresar una URL valida de video (http/https).');
        return;
      }

      if (newExercise.tieneAnimacion && !isHttpUrl(newExercise.urlGif)) {
        alert('Debes ingresar una URL valida para el GIF (http/https).');
        return;
      }

      if (newExercise.tieneVideoSubido) {
        if (!newExerciseVideoFile) {
          alert(`Debes subir un video (máximo ${MAX_EXERCISE_VIDEO_SIZE_MB} MB).`);
          return;
        }
        const uploaded = await uploadExerciseVideo(newExerciseVideoFile);
        uploadedVideoUrl = uploaded.publicUrl;
        uploadedVideoPath = uploaded.path;
      }

      const payload = {
        gimnasio_id: sucursalActiva.gimnasio_id,
        nombre: newExercise.nombre,
        categoria: newExercise.categoria,
        equipo: newExercise.equipo,
        dificultad: newExercise.dificultad,
        descripcion: newExercise.descripcion || null,
        musculos_principales: newExercise.musculosPrincipales || [],
        instrucciones: (newExercise.instrucciones || []).map(String).map(s => s.trim()).filter(Boolean),
        consejos: (newExercise.consejos || []).map(String).map(s => s.trim()).filter(Boolean),
        tiene_video: newExercise.tieneVideo,
        tiene_animacion: newExercise.tieneAnimacion,
        tiene_video_subido: newExercise.tieneVideoSubido,
        url_video: newExercise.tieneVideo ? (newExercise.urlVideo || null) : null,
        url_gif: newExercise.tieneAnimacion ? (newExercise.urlGif || null) : null,
        url_video_subido: newExercise.tieneVideoSubido ? uploadedVideoUrl : null,
        video_subido_path: newExercise.tieneVideoSubido ? uploadedVideoPath : null,
      };

      let { data, error } = await supabase
        .from('ejercicios')
        .insert(payload)
        .select('*')
        .single();

      // Backward compat: if the DB hasn't been migrated yet, retry without new columns.
      if (error) {
        const msg = String((error as any)?.message || '');
        if (
          (
            msg.includes('instrucciones') ||
            msg.includes('consejos') ||
            msg.includes('tiene_video_subido') ||
            msg.includes('url_video_subido') ||
            msg.includes('video_subido_path')
          ) &&
          (msg.toLowerCase().includes('schema cache') || msg.toLowerCase().includes('could not find'))
        ) {
          const {
            instrucciones,
            consejos,
            tiene_video_subido,
            url_video_subido,
            video_subido_path,
            ...fallbackPayload
          } = payload as any;
          const retry = await supabase
            .from('ejercicios')
            .insert(fallbackPayload)
            .select('*')
            .single();
          data = retry.data;
          error = retry.error as any;
        }
      }

      if (error) throw error;

      const inserted = ejercicioFromRow(data as any);
      setEjercicios(prev => [inserted, ...prev]);
      setIsNewExerciseModalOpen(false);
      setNewExerciseVideoFile(null);
    } catch (err: any) {
      console.error('Error creando ejercicio:', err);
      alert('Error al crear el ejercicio: ' + (err.message || ''));
    }
  };

  const handleAddSesion = () => {
    const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nextLetter = letras[sesiones.length];
    if (sesiones.length < 26) {
      setSesiones(prev => [...prev, {
        id: nextLetter,
        nombre: `Sesión ${nextLetter}`,
        ejercicios: []
      }]);
    }
  };

  const handleRemoveSesion = (sesionId: string) => {
    if (sesiones.length > 1) {
      setSesiones(prev => prev.filter(s => s.id !== sesionId));
      if (sesionActiva === sesionId) {
        setSesionActiva(sesiones[0].id);
      }
    }
  };

  const handleRenameSesion = (sesionId: string, nuevoNombre: string) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionId ? { ...s, nombre: nuevoNombre } : s
    ));
  };

  const handleAddEjercicioToRutina = (ejercicio: Ejercicio) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? {
            ...s,
            ejercicios: [...s.ejercicios, {
              ejercicioId: ejercicio.id,
              nombre: ejercicio.nombre,
              categoria: ejercicio.categoria,
              series: 3,
              repeticiones: 12,
              descanso: 60
            }]
          }
        : s
    ));
  };

  const handleRemoveEjercicioFromRutina = (ejercicioId: number) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? { ...s, ejercicios: s.ejercicios.filter(e => e.ejercicioId !== ejercicioId) }
        : s
    ));
  };

  const handleUpdateEjercicioEnRutina = (ejercicioId: number, field: keyof EjercicioEnRutina, value: number) => {
    setSesiones(prev => prev.map(s => 
      s.id === sesionActiva 
        ? {
            ...s,
            ejercicios: s.ejercicios.map(e => 
              e.ejercicioId === ejercicioId ? { ...e, [field]: value } : e
            )
          }
        : s
    ));
  };

  const handleMoveEjercicio = (index: number, direction: 'up' | 'down') => {
    const sesionActual = sesiones.find(s => s.id === sesionActiva);
    if (!sesionActual) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sesionActual.ejercicios.length) return;
    
    setSesiones(prev => prev.map(s => {
      if (s.id === sesionActiva) {
        const newList = [...s.ejercicios];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        return { ...s, ejercicios: newList };
      }
      return s;
    }));
  };

  const handleSaveRutina = async () => {
    const totalEjercicios = sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0);
    if (!newRutina.nombre || !newRutina.nivel || totalEjercicios === 0) {
      return;
    }

    if (!sucursalActiva?.id) {
      alert('Debes seleccionar una sucursal para crear rutinas');
      return;
    }

    try {
      // Use a user from the active branch as routine owner.
      const { data: usuarios } = await supabase
        .from('usuarios')
        .select('id')
        .eq('sucursal_id', sucursalActiva.id)
        .limit(1)
        .maybeSingle();

      if (!usuarios) {
        alert('No hay usuarios disponibles');
        return;
      }

      const ejerciciosData = {
        nivel: newRutina.nivel,
        duracion: newRutina.duracion,
        sesiones: sesiones
      };

      const { data, error } = await supabase
        .from('rutinas')
        .insert({
          usuario_id: usuarios.id,
          gimnasio_id: sucursalActiva.gimnasio_id,
          nombre: newRutina.nombre,
          descripcion: newRutina.descripcion,
          ejercicios: ejerciciosData,
          dias_semana: sesiones.map(s => s.id),
          activa: newRutina.activa,
          es_plantilla: isTemplateMode,
          plantilla_global: isTemplateMode ? templateGlobalScope : false,
          creada_por_usuario: false
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Refresh list
      await fetchRutinas();
      setIsNewRutinaModalOpen(false);
    } catch (err: any) {
      console.error('Error saving rutina:', err);
      alert('Error al guardar la rutina: ' + err.message);
    }
  };

  const handleOpenAssignModal = (rutina: Rutina) => {
    setSelectedRutinaForAssign(rutina);
    // Pre-seleccionar usuarios ya asignados
    setSelectedUserIds(rutina.usuarios_asignados || []);
    setUserSearchTerm('');
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedRutinaForAssign(null);
    setSelectedUserIds([]);
    setUserSearchTerm('');
  };

  const handleToggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleToggleAllUsers = () => {
    const filteredUsers = usuarios.filter(usuario =>
      usuario.nombre.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
    
    const filteredUserIds = filteredUsers.map(u => u.id);
    
    // Si todos los usuarios filtrados están seleccionados, deseleccionar todos
    const allSelected = filteredUserIds.every(id => selectedUserIds.includes(id));
    
    if (allSelected) {
      // Remover solo los IDs de usuarios filtrados
      setSelectedUserIds(prev => prev.filter(id => !filteredUserIds.includes(id)));
    } else {
      // Agregar los IDs de usuarios filtrados que no están seleccionados
      setSelectedUserIds(prev => {
        const newIds = filteredUserIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleAssignRutina = async () => {
    if (selectedUserIds.length === 0 || !selectedRutinaForAssign) return;

    try {
      setError(null);

      // Actualizar la rutina en Supabase con los usuarios asignados
      const { error: updateError } = await supabase
        .from('rutinas')
        .update({
          usuarios_asignados: selectedUserIds,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRutinaForAssign.id);

      if (updateError) throw updateError;

      // Actualizar el estado local
      setRutinas(prev => prev.map(r => 
        r.id === selectedRutinaForAssign.id 
          ? { 
              ...r, 
              usuariosAsignados: selectedUserIds.length,
              usuarios_asignados: selectedUserIds
            }
          : r
      ));

      // Mostrar notificación de éxito
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);

      // Cerrar modal
      handleCloseAssignModal();
    } catch (err: any) {
      console.error('Error asignando rutina:', err);
      setError('Error al asignar la rutina: ' + err.message);
    }
  };

  const filteredUsersForAssign = usuarios.filter(usuario =>
    usuario.nombre.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const allFilteredUsersSelected = filteredUsersForAssign.length > 0 && 
    filteredUsersForAssign.every(u => selectedUserIds.includes(u.id));

  const sesionActual = sesiones.find(s => s.id === sesionActiva);

  // Obtener sesiones de la rutina seleccionada para detalles
  const getRutinaSesiones = (rutinaId: number): Sesion[] => {
    if (selectedRutinaForDetail?.id === rutinaId && selectedRutinaForDetail.sesiones?.length) {
      return selectedRutinaForDetail.sesiones;
    }

    const rutina = rutinas.find(r => r.id === rutinaId);
    if (rutina?.sesiones?.length) return rutina.sesiones;

    const plantilla = plantillas.find(p => p.id === rutinaId);
    if (plantilla?.sesiones?.length) return plantilla.sesiones;

    return sesionesEjemplo[rutinaId] || [];
  };

  type EjercicioRow = {
    id: number;
    gimnasio_id: string;
    nombre: string;
    categoria: string;
    equipo: string;
    dificultad: string;
    descripcion: string | null;
    musculos_principales: string[] | null;
    instrucciones: string[] | null;
    consejos: string[] | null;
    tiene_video: boolean | null;
    tiene_animacion: boolean | null;
    tiene_video_subido: boolean | null;
    url_video: string | null;
    url_gif: string | null;
    url_video_subido: string | null;
    video_subido_path: string | null;
    duracion_video_segundos: number | null;
    created_at: string;
    updated_at: string;
  };

  const ejercicioFromRow = (row: EjercicioRow): Ejercicio => ({
    id: row.id,
    nombre: row.nombre,
    categoria: row.categoria,
    equipo: row.equipo || '',
    dificultad: row.dificultad,
    descripcion: row.descripcion || '',
    musculosPrincipales: row.musculos_principales || [],
    instrucciones: row.instrucciones || [],
    consejos: row.consejos || [],
    tieneVideo: row.tiene_video ?? false,
    tieneAnimacion: row.tiene_animacion ?? false,
    tieneVideoSubido: row.tiene_video_subido ?? false,
    urlVideo: row.url_video || '',
    urlGif: row.url_gif || '',
    urlVideoSubido: row.url_video_subido || '',
    videoSubidoPath: row.video_subido_path || '',
    duracionVideoSegundos: row.duracion_video_segundos ?? null,
  });

  const fetchEjercicios = async () => {
    if (!sucursalActiva?.gimnasio_id) {
      setEjercicios([]);
      setErrorEjercicios(null);
      setLoadingEjercicios(false);
      return;
    }

    setLoadingEjercicios(true);
    setErrorEjercicios(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('ejercicios')
        .select('*')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .order('nombre', { ascending: true });

      if (fetchError) throw fetchError;

      setEjercicios((data || []).map((r: any) => ejercicioFromRow(r as EjercicioRow)));
    } catch (err: any) {
      console.error('Error fetching ejercicios:', err);
      setEjercicios([]);
      setErrorEjercicios(err.message || 'Error al cargar ejercicios');
    } finally {
      setLoadingEjercicios(false);
    }
  };

  // Fetch rutinas from Supabase
  useEffect(() => {
    fetchRutinas();
  }, [sucursalActiva?.id]);

  useEffect(() => {
    fetchEjercicios();
  }, [sucursalActiva?.gimnasio_id]);

  // Fetch usuarios from Supabase
  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      console.log('No hay sucursal activa');
      setUsuarios([]);
      setLoadingUsuarios(false);
      return;
    }

    setLoadingUsuarios(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado')
        .eq('sucursal_id', sucursalActiva.id);

      if (error) throw error;

      const transformedUsuarios = (data || []).map((usuario: any) => {
        const initials = usuario.nombre
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        return {
          id: usuario.id,
          nombre: usuario.nombre,
          email: usuario.email,
          avatar: initials,
          plan: usuario.tipo_membresia || 'Sin plan',
          estado: usuario.estado || 'Activo'
        };
      });

      setUsuarios(transformedUsuarios);
    } catch (err: any) {
      console.error('Error fetching usuarios:', err);
      setUsuarios([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Fetch rutinas when assign modal opens
  useEffect(() => {
    if (isAssignModalOpen) {
      fetchUsuarios();
    }
  }, [isAssignModalOpen, sucursalActiva]);

  const fetchRutinas = async () => {
    if (!sucursalActiva?.id) {
      setRutinas([]);
      setPlantillas([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: usuariosSucursal, error: usuariosError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('sucursal_id', sucursalActiva.id);

      if (usuariosError) throw usuariosError;

      const usuariosIds = (usuariosSucursal || []).map((u: any) => u.id).filter(Boolean);

      const { data: usuariosGym, error: usuariosGymError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id);

      if (usuariosGymError) throw usuariosGymError;

      const usuariosGymIds = (usuariosGym || []).map((u: any) => u.id).filter(Boolean);

      const mapRutina = (rutina: any): Rutina => {
        const ejerciciosData = rutina.ejercicios || { sesiones: [] };
        const sesiones = ejerciciosData.sesiones || [];
        const totalEjercicios = sesiones.reduce((sum: number, s: any) => sum + (s.ejercicios?.length || 0), 0);
        const usuariosAsignados = Array.isArray(rutina.usuarios_asignados) ? rutina.usuarios_asignados : [];

        return {
          id: rutina.id,
          nombre: rutina.nombre,
          descripcion: rutina.descripcion || '',
          nivel: ejerciciosData.nivel || 'Intermedio',
          duracion: ejerciciosData.duracion || 45,
          ejercicios: totalEjercicios,
          usuariosAsignados: usuariosAsignados.length,
          activa: rutina.activa,
          es_plantilla: rutina.es_plantilla === true,
          plantilla_global: rutina.plantilla_global === true,
          creada_por_usuario: rutina.creada_por_usuario === true,
          sesiones: sesiones,
          usuarios_asignados: usuariosAsignados
        };
      };

      if (usuariosIds.length === 0) {
        setRutinas([]);
      } else {
        const { data, error: fetchError } = await supabase
          .from('rutinas')
          .select(`
            *,
            usuarios!rutinas_usuario_id_fkey(nombre, email)
          `)
          .in('usuario_id', usuariosIds)
          .or('es_plantilla.is.null,es_plantilla.eq.false')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        const transformedRutinas: Rutina[] = (data || []).map(mapRutina);
        setRutinas(transformedRutinas);
      }

      let plantillasQuery = supabase
        .from('rutinas')
        .select(`
          *,
          usuarios!rutinas_usuario_id_fkey(nombre, email)
        `)
        .eq('es_plantilla', true)
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .order('created_at', { ascending: false });

      const { data: dataPlantillas, error: fetchPlantillasError } = await plantillasQuery;

      if (fetchPlantillasError) throw fetchPlantillasError;

      const transformedPlantillas: Rutina[] = (dataPlantillas || []).map(mapRutina);
      setPlantillas(transformedPlantillas);
    } catch (err: any) {
      console.error('Error fetching rutinas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedCount = (rutina: Rutina) => {
    if (Array.isArray(rutina.usuarios_asignados)) {
      return rutina.usuarios_asignados.map(String).map(s => s.trim()).filter(Boolean).length;
    }
    return rutina.usuariosAsignados || 0;
  };

  const handleDeleteRutina = async (rutina: Rutina) => {
    const assignedCount = getAssignedCount(rutina);
    if (assignedCount > 0) {
      alert('No puedes eliminar esta rutina porque tiene usuarios asignados. Primero desasigna a todos los usuarios.');
      return;
    }

    const ok = confirm(`¿Eliminar la rutina "${rutina.nombre}"?\n\nEsta acción no se puede deshacer.`);
    if (!ok) return;

    try {
      setDeletingRutinaId(rutina.id);
      const { error: deleteError } = await supabase
        .from('rutinas')
        .delete()
        .eq('id', rutina.id);

      if (deleteError) throw deleteError;

      setRutinas(prev => prev.filter(r => r.id !== rutina.id));

      if (selectedRutinaForDetail?.id === rutina.id) {
        handleCloseDetailModal();
      }
    } catch (err: any) {
      console.error('Error eliminando rutina:', err);
      const msg = String(err?.message || '');
      if (msg.toLowerCase().includes('usuarios asignados')) {
        alert('No puedes eliminar esta rutina porque tiene usuarios asignados. Primero desasigna a todos los usuarios.');
      } else {
        alert('Error al eliminar la rutina: ' + (err?.message || ''));
      }
    } finally {
      setDeletingRutinaId(null);
    }
  };

  // Add function to fetch usuarios asignados
  const fetchUsuariosAsignados = async (usuarioIds: string[]) => {
    setLoadingUsuariosDetail(true);
    try {
      let query = supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado, fecha_inicio, fecha_vencimiento')
        .in('id', usuarioIds);

      if (sucursalActiva?.id) {
        query = query.eq('sucursal_id', sucursalActiva.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedUsuarios = (data || []).map((usuario: any) => {
        const initials = usuario.nombre
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        // Calculate progress (example: days since start / 30 days)
        const fechaInicio = usuario.fecha_inicio ? new Date(usuario.fecha_inicio) : new Date();
        const hoy = new Date();
        const diasTranscurridos = Math.floor((hoy.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24));
        const progreso = Math.min(Math.floor((diasTranscurridos / 30) * 100), 100);

        return {
          id: usuario.id,
          nombre: usuario.nombre,
          email: usuario.email,
          avatar: initials,
          plan: usuario.tipo_membresia || 'Sin plan',
          estado: usuario.estado || 'Activo',
          fechaAsignacion: usuario.fecha_inicio ? new Date(usuario.fecha_inicio).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES'),
          progreso: progreso
        };
      });

      setUsuariosAsignadosDetail(transformedUsuarios);
    } catch (err: any) {
      console.error('Error fetching usuarios asignados:', err);
      setUsuariosAsignadosDetail([]);
    } finally {
      setLoadingUsuariosDetail(false);
    }
  };

  // Handle user menu actions
  const handleViewUserProfile = (userId: string) => {
    setActiveUserMenu(null);
    // Navigate to user profile
    window.REACT_APP_NAVIGATE(`/usuarios?id=${userId}`);
  };

  const handleViewUserProgress = (userId: string) => {
    setActiveUserMenu(null);
    // Navigate to progress page with user filter
    window.REACT_APP_NAVIGATE(`/progreso?usuario=${userId}`);
  };

  const handleUnassignUser = async (userId: string) => {
    if (!selectedRutinaForDetail) return;
    
    const confirmUnassign = confirm('¿Estás seguro de que deseas desasignar este usuario de la rutina?');
    if (!confirmUnassign) {
      setActiveUserMenu(null);
      return;
    }

    try {
      // Remove user from assigned list
      const newUsuariosAsignados = (selectedRutinaForDetail.usuarios_asignados || []).filter(id => id !== userId);
      
      const { error } = await supabase
        .from('rutinas')
        .update({
          usuarios_asignados: newUsuariosAsignados,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRutinaForDetail.id);

      if (error) throw error;

      // Update local state
      setUsuariosAsignadosDetail(prev => prev.filter(u => u.id !== userId));
      setSelectedRutinaForDetail(prev => prev ? {
        ...prev,
        usuariosAsignados: newUsuariosAsignados.length,
        usuarios_asignados: newUsuariosAsignados
      } : null);
      
      // Update rutinas list
      setRutinas(prev => prev.map(r => 
        r.id === selectedRutinaForDetail.id 
          ? { ...r, usuariosAsignados: newUsuariosAsignados.length, usuarios_asignados: newUsuariosAsignados }
          : r
      ));

      setActiveUserMenu(null);
    } catch (err: any) {
      console.error('Error desasignando usuario:', err);
      alert('Error al desasignar el usuario: ' + err.message);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando rutinas...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line text-red-600 text-2xl"></i>
            <div>
              <h3 className="text-red-900 font-semibold">Error al cargar rutinas</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold">¡Rutina asignada exitosamente!</p>
              <p className="text-sm text-green-100">
                {selectedUserIds.length} usuario{selectedUserIds.length !== 1 ? 's' : ''} asignado{selectedUserIds.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1 inline-flex">
        <button
          onClick={() => setActiveTab('rutinas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'rutinas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Rutinas
        </button>
        <button
          onClick={() => setActiveTab('ejercicios')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'ejercicios'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Biblioteca de Ejercicios
        </button>
        <button
          onClick={() => setActiveTab('plantillas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'plantillas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Plantillas
        </button>
        <button
          onClick={() => setActiveTab('personalizadas')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
            activeTab === 'personalizadas'
              ? 'bg-teal-600 text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Personalizadas
        </button>
      </div>

      {/* Search and Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder={
                activeTab === 'rutinas'
                  ? 'Buscar rutinas...'
                  : activeTab === 'plantillas'
                    ? 'Buscar plantillas...'
                    : activeTab === 'personalizadas'
                      ? 'Buscar rutinas personalizadas...'
                    : 'Buscar ejercicios...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <button 
            onClick={handleButtonClick}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            {activeTab === 'rutinas'
              ? 'Nueva Rutina'
              : activeTab === 'plantillas'
                ? 'Nueva Plantilla'
                : activeTab === 'personalizadas'
                  ? 'Nueva Rutina'
                : 'Nuevo Ejercicio'}
          </button>
        </div>
      </div>

      {activeTab !== 'ejercicios' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(activeTab === 'plantillas'
            ? filteredPlantillas
            : activeTab === 'personalizadas'
              ? filteredRutinasPersonalizadas
              : filteredRutinas
          ).map((rutina) => (
            <div key={rutina.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{rutina.nombre}</h3>
                    <p className="text-sm text-gray-600">{rutina.descripcion}</p>
                    {activeTab !== 'plantillas' && rutina.creada_por_usuario && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        <i className="ri-user-star-line"></i>
                        Personalizada por usuario
                      </span>
                    )}
                    {(rutina.sesiones || sesionesEjemplo[rutina.id]) && (
                      <div className="flex gap-1 mt-2">
                        {(rutina.sesiones || sesionesEjemplo[rutina.id])?.map(sesion => (
                          <span key={sesion.id} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                            {sesion.id}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`w-3 h-3 rounded-full ${rutina.activa ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-time-line text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">Duración</p>
                    <p className="text-sm font-semibold text-gray-900">{rutina.duracion} min</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-list-check text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">Ejercicios</p>
                    <p className="text-sm font-semibold text-gray-900">{rutina.ejercicios}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <i className="ri-user-line text-teal-600 text-xl mb-1"></i>
                    <p className="text-xs text-gray-600">{activeTab === 'plantillas' ? 'Alcance' : 'Usuarios'}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {activeTab === 'plantillas'
                        ? (rutina.plantilla_global ? 'Global' : 'Interna')
                        : rutina.usuariosAsignados}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    rutina.nivel === 'Principiante' ? 'bg-green-100 text-green-800' :
                    rutina.nivel === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {rutina.nivel}
                  </span>
                  <div className="flex gap-2">
                    {activeTab !== 'plantillas' && (
                      <button 
                        onClick={() => handleOpenAssignModal(rutina)}
                        className="px-4 py-2 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-user-add-line mr-1"></i>
                        Asignar
                      </button>
                    )}
                    {activeTab === 'plantillas' && (
                      <button
                        onClick={() => handleExportPlantillaToRutina(rutina)}
                        className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-share-forward-line mr-1"></i>
                        Exportar
                      </button>
                    )}
                    <button 
                      onClick={() => handleOpenDetailModal(rutina)}
                      className="px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Ver Detalles
                    </button>
                    <button
                      onClick={() => handleDeleteRutina(rutina)}
                      disabled={getAssignedCount(rutina) > 0 || deletingRutinaId === rutina.id}
                      title={
                        getAssignedCount(rutina) > 0
                          ? 'No se puede eliminar: tiene usuarios asignados'
                          : 'Eliminar rutina'
                      }
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors whitespace-nowrap ${
                        getAssignedCount(rutina) > 0 || deletingRutinaId === rutina.id
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer'
                      }`}
                      aria-label="Eliminar rutina"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ejercicio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dificultad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Músculos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recursos
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingEjercicios ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"></span>
                        Cargando ejercicios...
                      </div>
                    </td>
                  </tr>
                ) : errorEjercicios ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-red-600">
                      {errorEjercicios}
                    </td>
                  </tr>
                ) : filteredEjercicios.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      No hay ejercicios en la biblioteca para este gimnasio.
                    </td>
                  </tr>
                ) : (
                  filteredEjercicios.map((ejercicio) => (
                    <tr key={ejercicio.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{ejercicio.nombre}</div>
                          <div className="text-sm text-gray-500">{ejercicio.descripcion}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800">
                          {ejercicio.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ejercicio.equipo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-800' :
                          ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {ejercicio.dificultad}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {ejercicio.musculosPrincipales.slice(0, 2).join(', ')}
                          {ejercicio.musculosPrincipales.length > 2 && '...'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {ejercicio.tieneVideo && (
                            <span className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600 text-sm"></i>
                            </span>
                          )}
                          {ejercicio.tieneAnimacion && (
                            <span className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600 text-sm"></i>
                            </span>
                          )}
                          {ejercicio.tieneVideoSubido && (
                            <span className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600 text-sm"></i>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleOpenExerciseDetailModal(ejercicio)}
                          className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer"
                        >
                          <i className="ri-eye-line w-5 h-5 flex items-center justify-center"></i>
                        </button>
                        <button 
                          onClick={() => handleOpenEditExerciseModal(ejercicio)}
                          className="text-gray-600 hover:text-gray-900 cursor-pointer"
                        >
                          <i className="ri-edit-line w-5 h-5 flex items-center justify-center"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Exercise Detail Modal */}
      {isExerciseDetailModalOpen && selectedExerciseForDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseExerciseDetailModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <i className="ri-boxing-line text-teal-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedExerciseForDetail.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        {selectedExerciseForDetail.categoria}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedExerciseForDetail.dificultad === 'Principiante' ? 'bg-green-100 text-green-800' :
                        selectedExerciseForDetail.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedExerciseForDetail.dificultad}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleCloseExerciseDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Descripción */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-file-text-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Descripción
                  </h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    {selectedExerciseForDetail.descripcion || 'Sin descripción disponible.'}
                  </p>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-price-tag-3-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Categoría</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.categoria}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-tools-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Equipo</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.equipo || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <i className="ri-bar-chart-line text-2xl text-teal-600 mb-2"></i>
                    <p className="text-xs text-gray-500 mb-1">Dificultad</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExerciseForDetail.dificultad}</p>
                  </div>
                </div>

                {/* Músculos Principales */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-body-scan-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Músculos Principales
                  </h4>
                  {selectedExerciseForDetail.musculosPrincipales.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedExerciseForDetail.musculosPrincipales.map((musculo) => (
                        <span key={musculo} className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                          {musculo}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No se han especificado músculos.</p>
                  )}
                </div>

                {/* Recursos Multimedia */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-movie-2-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Recursos Multimedia
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneVideo ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneVideo ? 'bg-blue-200' : 'bg-gray-200'}`}>
                          <i className={`ri-video-line text-lg ${selectedExerciseForDetail.tieneVideo ? 'text-blue-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Video</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneVideo ? 'text-blue-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneVideo ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneVideo && selectedExerciseForDetail.urlVideo && (
                        <a 
                          href={selectedExerciseForDetail.urlVideo} 
                          target="_blank" 
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver video
                        </a>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneAnimacion ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneAnimacion ? 'bg-green-200' : 'bg-gray-200'}`}>
                          <i className={`ri-play-circle-line text-lg ${selectedExerciseForDetail.tieneAnimacion ? 'text-green-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Animación</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneAnimacion ? 'text-green-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneAnimacion ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneAnimacion && selectedExerciseForDetail.urlGif && (
                        <a 
                          href={selectedExerciseForDetail.urlGif} 
                          target="_blank" 
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver animación
                        </a>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedExerciseForDetail.tieneVideoSubido ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedExerciseForDetail.tieneVideoSubido ? 'bg-purple-200' : 'bg-gray-200'}`}>
                          <i className={`ri-film-line text-lg ${selectedExerciseForDetail.tieneVideoSubido ? 'text-purple-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Video subido</p>
                          <p className={`text-xs ${selectedExerciseForDetail.tieneVideoSubido ? 'text-purple-600' : 'text-gray-400'}`}>
                            {selectedExerciseForDetail.tieneVideoSubido ? 'Disponible' : 'No disponible'}
                          </p>
                        </div>
                      </div>
                      {selectedExerciseForDetail.tieneVideoSubido && selectedExerciseForDetail.urlVideoSubido && (
                        <a
                          href={selectedExerciseForDetail.urlVideoSubido}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline cursor-pointer mt-1"
                        >
                          <i className="ri-external-link-line"></i>
                          Ver video (loop)
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rutinas que usan este ejercicio */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                    Rutinas que incluyen este ejercicio
                  </h4>
                  {(() => {
                    const rutinasConEjercicio = rutinas.filter(r => {
                      const sesionesR = r.sesiones || sesionesEjemplo[r.id] || [];
                      return sesionesR.some(s => s.ejercicios.some(e => e.ejercicioId === selectedExerciseForDetail.id));
                    });
                    return rutinasConEjercicio.length > 0 ? (
                      <div className="space-y-2">
                        {rutinasConEjercicio.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                                {r.nombre.charAt(0)}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{r.nombre}</p>
                                <p className="text-xs text-gray-500">{r.nivel} &middot; {r.duracion} min</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                handleCloseExerciseDetailModal();
                                handleOpenDetailModal(r);
                              }}
                              className="px-3 py-1.5 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Ver rutina
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-lg">
                        Este ejercicio no est? asignado a ninguna rutina actualmente.
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseExerciseDetailModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
                <button 
                  onClick={() => {
                    handleCloseExerciseDetailModal();
                    handleOpenEditExerciseModal(selectedExerciseForDetail);
                  }}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Editar Ejercicio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {isAssignModalOpen && selectedRutinaForAssign && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseAssignModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-3xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Asignar Rutina</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedRutinaForAssign.nombre}</p>
                </div>
                <button 
                  onClick={handleCloseAssignModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Buscar por nombre o email..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Info Box */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-blue-600 mt-0.5"></i>
                    <div className="text-xs text-blue-700">
                      <p className="font-medium mb-1">Sobre esta rutina:</p>
                      {(() => {
                        const sesionesRutina = selectedRutinaForAssign.sesiones || sesionesEjemplo[selectedRutinaForAssign.id] || [];
                        return sesionesRutina.length > 0 ? (
                          <>
                            <p className="text-xs text-blue-700 mb-2">
                              Esta rutina tiene {sesionesRutina.length} sesiones que se alternan durante la semana.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {sesionesRutina.map(sesion => (
                                <span key={sesion.id} className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
                                  {sesion.nombre} ({sesion.ejercicios.length} ejercicios)
                                </span>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-blue-700">
                            Esta rutina contiene {selectedRutinaForAssign.ejercicios} ejercicios.
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Users List */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedUserIds.length} de {usuarios.length} usuarios seleccionados
                      </span>
                      <button
                        onClick={handleToggleAllUsers}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        {allFilteredUsersSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      </button>
                    </div>
                  </div>

                  {loadingUsuarios ? (
                    <div className="p-8 text-center">
                      <i className="ri-loader-4-line text-3xl text-teal-500 animate-spin"></i>
                      <p className="text-sm text-gray-500 mt-2">Cargando usuarios...</p>
                    </div>
                  ) : filteredUsersForAssign.length === 0 ? (
                    <div className="p-8 text-center">
                      <i className="ri-user-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-500">No se encontraron usuarios</p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      {filteredUsersForAssign.map((usuario) => (
                        <label
                          key={usuario.id}
                          className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(usuario.id)}
                            onChange={() => handleToggleUserSelection(usuario.id)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-teal-600">{usuario.avatar}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{usuario.nombre}</p>
                            <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium whitespace-nowrap">
                              {usuario.plan}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                              usuario.estado === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {usuario.estado}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseAssignModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleAssignRutina}
                  disabled={selectedUserIds.length === 0}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Asignar a {selectedUserIds.length} usuario{selectedUserIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedRutinaForDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseDetailModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-5xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-teal-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedRutinaForDetail.nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedRutinaForDetail.nivel === 'Principiante' ? 'bg-green-100 text-green-800' :
                        selectedRutinaForDetail.nivel === 'Intermedio' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedRutinaForDetail.nivel}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedRutinaForDetail.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedRutinaForDetail.activa ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleCloseDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-4 border-b border-gray-200">
                <div className="flex gap-4">
                  {[
                    { id: 'info', label: 'Información', icon: 'ri-information-line' },
                    { id: 'sesiones', label: 'Sesiones', icon: 'ri-list-check' },
                    { id: 'usuarios', label: 'Usuarios Asignados', icon: 'ri-user-line' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setDetailActiveTab(tab.id as 'info' | 'sesiones' | 'usuarios')}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                        detailActiveTab === tab.id
                          ? 'border-teal-600 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={tab.icon}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 max-h-[60vh] min-h-[50vh] overflow-y-auto">
                {detailActiveTab === 'info' && (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-time-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.duracion}</p>
                        <p className="text-xs text-gray-500">Minutos por sesión</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-list-check text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.ejercicios}</p>
                        <p className="text-xs text-gray-500">Ejercicios totales</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-calendar-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{getRutinaSesiones(selectedRutinaForDetail.id).length}</p>
                        <p className="text-xs text-gray-500">Sesiones</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <i className="ri-user-line text-2xl text-teal-600 mb-2"></i>
                        <p className="text-2xl font-bold text-gray-900">{selectedRutinaForDetail.usuariosAsignados}</p>
                        <p className="text-xs text-gray-500">Usuarios asignados</p>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Descripción</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                        {selectedRutinaForDetail.descripcion || 'Sin descripción disponible.'}
                      </p>
                    </div>

                    {/* Resumen de sesiones */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumen de Sesiones</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {getRutinaSesiones(selectedRutinaForDetail.id).map((sesion) => (
                          <div key={sesion.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                                {sesion.id}
                              </span>
                              <span className="text-sm font-medium text-gray-900">{sesion.nombre}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span><i className="ri-list-check mr-1"></i>{sesion.ejercicios.length} ejercicios</span>
                              <span><i className="ri-time-line mr-1"></i>~{sesion.ejercicios.reduce((sum, e) => sum + (e.series * e.repeticiones * 3 + e.descanso * e.series) / 60, 0).toFixed(0)} min</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Músculos trabajados */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Músculos Trabajados</h4>
                      <div className="flex flex-wrap gap-2">
                        {Array.from(new Set(
                          getRutinaSesiones(selectedRutinaForDetail.id)
                            .flatMap(s => s.ejercicios)
                            .map(e => e.categoria)
                        )).map((categoria) => (
                          <span key={categoria} className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                            {categoria}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {detailActiveTab === 'sesiones' && (
                  <div className="space-y-4">
                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {getRutinaSesiones(selectedRutinaForDetail.id).map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setDetailActiveSesion(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            detailActiveSesion === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Session Exercises */}
                    {getRutinaSesiones(selectedRutinaForDetail.id)
                      .filter(s => s.id === detailActiveSesion)
                      .map((sesion) => (
                        <div key={sesion.id} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">{sesion.nombre}</h4>
                            <span className="text-xs text-gray-500">{sesion.ejercicios.length} ejercicios</span>
                          </div>
                          <div className="space-y-2">
                            {sesion.ejercicios.map((ej, index) => {
                              const ejercicioInfo = ejercicios.find(e => e.id === ej.ejercicioId);
                              return (
                                <div key={ej.ejercicioId} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                                        {index + 1}
                                      </span>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{ej.nombre}</p>
                                        <p className="text-xs text-gray-500">{ejercicioInfo?.descripcion}</p>
                                      </div>
                                    </div>
                                    <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                      {ej.categoria}
                                    </span>
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-4">
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.series}</p>
                                      <p className="text-xs text-gray-500">Series</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.repeticiones}</p>
                                      <p className="text-xs text-gray-500">Repeticiones</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">{ej.descanso}s</p>
                                      <p className="text-xs text-gray-500">Descanso</p>
                                    </div>
                                  </div>
                                  {ejercicioInfo && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <p className="text-xs text-gray-500 mb-2">Músculos trabajados:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {ejercicioInfo.musculosPrincipales.map((musculo) => (
                                          <span key={musculo} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">
                                            {musculo}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {detailActiveTab === 'usuarios' && (
                  <div className="space-y-6">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 relative">
                          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                          <input
                            type="text"
                            placeholder="Buscar por nombre o email..."
                            value={usuariosAsignadosSearchTerm}
                            onChange={(e) => {
                              setUsuariosAsignadosSearchTerm(e.target.value);
                              setUsuariosAsignadosPage(1);
                            }}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        <button 
                          onClick={() => {
                            handleCloseDetailModal();
                            handleOpenAssignModal(selectedRutinaForDetail);
                          }}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-add-line mr-2"></i>
                          Asignar Más
                        </button>
                      </div>
                      
                      {/* Filtros por Estado y Plan */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">Filtrar por:</span>
                        </div>
                        <select
                          value={usuariosAsignadosEstadoFilter}
                          onChange={(e) => {
                            setUsuariosAsignadosEstadoFilter(e.target.value);
                            setUsuariosAsignadosPage(1);
                          }}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white"
                        >
                          <option value="">Todos los estados</option>
                          <option value="activo">Activo</option>
                          <option value="inactivo">Inactivo</option>
                        </select>
                        <select
                          value={usuariosAsignadosPlanFilter}
                          onChange={(e) => {
                            setUsuariosAsignadosPlanFilter(e.target.value);
                            setUsuariosAsignadosPage(1);
                          }}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer bg-white"
                        >
                          <option value="">Todos los planes</option>
                          {Array.from(new Set(usuariosAsignadosDetail.map(u => u.plan))).filter(Boolean).map(plan => (
                            <option key={plan} value={plan}>{plan}</option>
                          ))}
                        </select>
                        {(usuariosAsignadosEstadoFilter || usuariosAsignadosPlanFilter) && (
                          <button
                            onClick={() => {
                              setUsuariosAsignadosEstadoFilter('');
                              setUsuariosAsignadosPlanFilter('');
                              setUsuariosAsignadosPage(1);
                            }}
                            className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                          >
                            <i className="ri-close-line"></i>
                            Limpiar filtros
                          </button>
                        )}
                      </div>
                    </div>

                    {loadingUsuariosDetail ? (
                      <div className="text-center py-16">
                        <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
                        <p className="text-sm text-gray-500 mt-3">Cargando usuarios...</p>
                      </div>
                    ) : usuariosAsignadosDetail.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-user-line text-3xl text-gray-300"></i>
                        </div>
                        <p className="text-sm text-gray-500 mb-1">No hay usuarios asignados a esta rutina</p>
                        <p className="text-xs text-gray-400 mb-4">Asigna usuarios para que puedan seguir esta rutina</p>
                        <button 
                          onClick={() => {
                            handleCloseDetailModal();
                            handleOpenAssignModal(selectedRutinaForDetail);
                          }}
                          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-add-line mr-2"></i>
                          Asignar Usuarios
                        </button>
                      </div>
                    ) : (() => {
                      const filteredUsuarios = usuariosAsignadosDetail.filter(usuario => {
                        const matchSearch = usuario.nombre.toLowerCase().includes(usuariosAsignadosSearchTerm.toLowerCase()) ||
                          usuario.email.toLowerCase().includes(usuariosAsignadosSearchTerm.toLowerCase());
                        const matchEstado = !usuariosAsignadosEstadoFilter || usuario.estado.toLowerCase() === usuariosAsignadosEstadoFilter.toLowerCase();
                        const matchPlan = !usuariosAsignadosPlanFilter || usuario.plan === usuariosAsignadosPlanFilter;
                        return matchSearch && matchEstado && matchPlan;
                      });
                      const totalPages = Math.ceil(filteredUsuarios.length / USUARIOS_PER_PAGE);
                      const startIndex = (usuariosAsignadosPage - 1) * USUARIOS_PER_PAGE;
                      const paginatedUsuarios = filteredUsuarios.slice(startIndex, startIndex + USUARIOS_PER_PAGE);

                      return filteredUsuarios.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-filter-off-line text-3xl text-gray-300"></i>
                          </div>
                          <p className="text-sm text-gray-500 mb-1">No se encontraron usuarios</p>
                          <p className="text-xs text-gray-400">Intenta con otros filtros o términos de búsqueda</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500">
                            Mostrando {startIndex + 1}-{Math.min(startIndex + USUARIOS_PER_PAGE, filteredUsuarios.length)} de {filteredUsuarios.length} usuarios
                            {(usuariosAsignadosEstadoFilter || usuariosAsignadosPlanFilter) && (
                              <span className="ml-2 text-teal-600">
                                (filtrado de {usuariosAsignadosDetail.length} total)
                              </span>
                            )}
                          </p>
                          {paginatedUsuarios.map((usuario) => (
                              <div key={usuario.id} className="p-5 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                                      <span className="text-sm font-semibold text-teal-600">{usuario.avatar}</span>
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">{usuario.nombre}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{usuario.email}</p>
                                      <div className="flex items-center gap-3 mt-1.5">
                                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                          {usuario.plan}
                                        </span>
                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                          <i className="ri-calendar-line text-xs"></i>
                                          Asignado: {usuario.fechaAsignacion}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="min-w-[160px]">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs text-gray-500">Progreso</span>
                                        <span className="text-sm font-bold text-gray-900">{usuario.progreso}%</span>
                                      </div>
                                      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full transition-all ${
                                            usuario.progreso! >= 75 ? 'bg-green-500' :
                                            usuario.progreso! >= 40 ? 'bg-teal-500' :
                                            'bg-orange-400'
                                          }`}
                                          style={{ width: `${usuario.progreso}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                      usuario.estado === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {usuario.estado}
                                    </span>
                                    <div className="relative">
                                      <button 
                                        onClick={() => setActiveUserMenu(activeUserMenu === usuario.id ? null : usuario.id)}
                                        className="w-9 h-9 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                                      >
                                        <i className="ri-more-2-fill text-gray-500 text-lg"></i>
                                      </button>
                                      
                                      {activeUserMenu === usuario.id && (
                                        <>
                                          <div 
                                            className="fixed inset-0 z-10"
                                            onClick={() => setActiveUserMenu(null)}
                                          ></div>
                                          <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 z-20">
                                            <button
                                              onClick={() => handleViewUserProfile(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                            >
                                              <i className="ri-user-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                                              Ver Perfil
                                            </button>
                                            <button
                                              onClick={() => handleViewUserProgress(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                            >
                                              <i className="ri-line-chart-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                                              Ver Progreso
                                            </button>
                                            <div className="border-t border-gray-100 my-1"></div>
                                            <button
                                              onClick={() => handleUnassignUser(usuario.id)}
                                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                                            >
                                              <i className="ri-user-unfollow-line w-5 h-5 flex items-center justify-center"></i>
                                              Desasignar Rutina
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                          ))}

                          {/* Pagination Controls */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                Página {usuariosAsignadosPage} de {totalPages}
                              </p>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setUsuariosAsignadosPage(1)}
                                  disabled={usuariosAsignadosPage === 1}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-skip-back-mini-line"></i>
                                </button>
                                <button
                                  onClick={() => setUsuariosAsignadosPage(prev => Math.max(1, prev - 1))}
                                  disabled={usuariosAsignadosPage === 1}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-arrow-left-s-line"></i>
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                  .filter(page => {
                                    if (totalPages <= 5) return true;
                                    if (page === 1 || page === totalPages) return true;
                                    return Math.abs(page - usuariosAsignadosPage) <= 1;
                                  })
                                  .map((page, idx, arr) => {
                                    const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                                    return (
                                      <span key={page} className="flex items-center">
                                        {showEllipsis && (
                                          <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>
                                        )}
                                        <button
                                          onClick={() => setUsuariosAsignadosPage(page)}
                                          className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                                            usuariosAsignadosPage === page
                                              ? 'bg-teal-600 text-white'
                                              : 'text-gray-600 hover:bg-gray-100'
                                          }`}
                                        >
                                          {page}
                                        </button>
                                      </span>
                                    );
                                  })}
                                <button
                                  onClick={() => setUsuariosAsignadosPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={usuariosAsignadosPage === totalPages}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-arrow-right-s-line"></i>
                                </button>
                                <button
                                  onClick={() => setUsuariosAsignadosPage(totalPages)}
                                  disabled={usuariosAsignadosPage === totalPages}
                                  className="w-8 h-8 flex items-center justify-center rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-skip-forward-mini-line"></i>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <i className="ri-calendar-line"></i>
                  <span>Creada el 15 de enero, 2024</span>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseDetailModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cerrar
                  </button>
                  <button 
                    onClick={() => handleOpenEditRutinaModal(selectedRutinaForDetail)}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Editar Rutina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rutina Modal */}
      {isEditRutinaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseEditRutinaModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedRutinaForEdit?.es_plantilla ? 'Editar Plantilla' : 'Editar Rutina'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedRutinaForEdit?.es_plantilla
                      ? 'Modifica la plantilla y define si queda global para todos los usuarios.'
                      : 'Modifica la información y ejercicios de la rutina'}
                  </p>
                </div>
                <button 
                  onClick={handleCloseEditRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editRutina.nombre}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={editRutina.descripcion}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setEditRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              editRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={editRutina.duracion}
                          onChange={(e) => setEditRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editRutina.activa}
                            onChange={(e) => setEditRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    {selectedRutinaForEdit?.es_plantilla && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editTemplateGlobalScope}
                            onChange={(e) => setEditTemplateGlobalScope(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Disponible para todos (global)</p>
                            <p className="text-xs text-blue-700">
                              Si está desmarcado, la plantilla queda interna para el equipo administrador.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({editSesiones.length})
                        </label>
                        <button
                          onClick={handleAddEditSesion}
                          disabled={editSesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editSesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameEditSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {editSesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveEditSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Session Exercises */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {editSesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setEditSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            editSesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Ejercicios de la sesión activa */}
                    {editSesionActual && (
                      <>
                        {editSesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {editSesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'down')}
                                      disabled={index === editSesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromEditRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      min={0}
                                      max={500}
                                      step={0.5}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={editEjercicioSearchTerm}
                          onChange={(e) => setEditEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={editCategoriaFiltro}
                        onChange={(e) => setEditCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponiblesEdit.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponiblesEdit.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToEditRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {editSesiones.length} sesión{editSesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseEditRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveEditRutina}
                    disabled={!editRutina.nombre || !editRutina.nivel || editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Exercise Modal */}
      {isEditExerciseModalOpen && selectedExerciseForEdit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseEditExerciseModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Editar Ejercicio</h3>
                  <p className="text-sm text-gray-500 mt-1">Modifica la información del ejercicio</p>
                </div>
                <button 
                  onClick={handleCloseEditExerciseModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ejercicio <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={selectedExerciseForEdit.nombre}
                      onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, nombre: e.target.value } : null)}
                      placeholder="Ej: Press de Banca"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={selectedExerciseForEdit.descripcion}
                      onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, descripcion: e.target.value } : null)}
                      placeholder="Describe brevemente el ejercicio..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedExerciseForEdit.categoria}
                        onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, categoria: e.target.value } : null)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Equipo Necesario
                      </label>
                      <select
                        value={selectedExerciseForEdit.equipo}
                        onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, equipo: e.target.value } : null)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {equipos.map(eq => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Nivel de Dificultad <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      {dificultades.map(dif => (
                        <button
                          key={dif}
                          type="button"
                          onClick={() => setSelectedExerciseForEdit(prev => prev ? { ...prev, dificultad: dif } : null)}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedExerciseForEdit.dificultad === dif
                               ? dif === 'Principiante' 
                                 ? 'bg-green-600 text-white' 
                                 : dif === 'Intermedio' 
                                   ? 'bg-orange-500 text-white' 
                                   : 'bg-red-600 text-white'
                               : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Músculos Principales
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {musculosDisponibles.map(musculo => (
                        <button
                          key={musculo}
                          type="button"
                          onClick={() => handleEditExerciseMusculoToggle(musculo)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedExerciseForEdit.musculosPrincipales.includes(musculo)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {musculo}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instrucciones (una por línea)
                      </label>
                      <textarea
                        value={(selectedExerciseForEdit.instrucciones || []).join('\n')}
                        onChange={(e) =>
                          setSelectedExerciseForEdit((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  instrucciones: e.target.value
                                    .split('\n')
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                }
                              : null
                          )
                        }
                        placeholder={"Ej:\nColoca la espalda recta\nTira la polea hacia el pecho\nControla el regreso"}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Consejos (opcional, uno por línea)
                      </label>
                      <textarea
                        value={(selectedExerciseForEdit.consejos || []).join('\n')}
                        onChange={(e) =>
                          setSelectedExerciseForEdit((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  consejos: e.target.value
                                    .split('\n')
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                }
                              : null
                          )
                        }
                        placeholder={"Ej:\nNo encorves la espalda\nNo uses impulso\nAjusta el peso para controlar"}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Si lo dejas vacío, en mobile se mostrarán consejos genéricos.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Recursos Multimedia
                    </label>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneVideo}
                            disabled={
                              !selectedExerciseForEdit.tieneVideo &&
                              (selectedExerciseForEdit.tieneAnimacion || selectedExerciseForEdit.tieneVideoSubido)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('video', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Explicativo</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneVideo && (
                          <div>
                            <input
                              type="url"
                              value={selectedExerciseForEdit.urlVideo || ''}
                              onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, urlVideo: e.target.value } : null)}
                              placeholder="https://youtube.com/watch?v=..."
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {selectedExerciseForEdit.urlVideo && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-700 mb-1">Vista previa:</p>
                                <a 
                                  href={selectedExerciseForEdit.urlVideo} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline break-all"
                                >
                                  {selectedExerciseForEdit.urlVideo}
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneAnimacion}
                            disabled={
                              !selectedExerciseForEdit.tieneAnimacion &&
                              (selectedExerciseForEdit.tieneVideo || selectedExerciseForEdit.tieneVideoSubido)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('gif', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Animación GIF</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneAnimacion && (
                          <div>
                            <input
                              type="url"
                              value={selectedExerciseForEdit.urlGif || ''}
                              onChange={(e) => setSelectedExerciseForEdit(prev => prev ? { ...prev, urlGif: e.target.value } : null)}
                              placeholder="https://ejemplo.com/animacion.gif"
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {selectedExerciseForEdit.urlGif && (
                              <div className="mt-2 p-3 bg-green-50 rounded-lg">
                                <p className="text-xs text-green-700 mb-2">Vista previa:</p>
                                <img 
                                  src={selectedExerciseForEdit.urlGif} 
                                  alt="Animación del ejercicio"
                                  className="w-full max-w-xs rounded-lg border border-green-200"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedExerciseForEdit.tieneVideoSubido}
                            disabled={
                              !selectedExerciseForEdit.tieneVideoSubido &&
                              (selectedExerciseForEdit.tieneVideo || selectedExerciseForEdit.tieneAnimacion)
                            }
                            onChange={(e) => handleToggleEditExerciseMedia('upload', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Subido (máx. 10 MB)</span>
                          </div>
                        </label>
                        {selectedExerciseForEdit.tieneVideoSubido && (
                          <div className="space-y-2">
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/ogg"
                              onChange={async (e) => {
                                const file = e.currentTarget.files?.[0] || null;
                                try {
                                  await handleEditExerciseVideoFileChange(file);
                                } catch (err: any) {
                                  alert(err?.message || 'No se pudo procesar el video.');
                                  e.currentTarget.value = '';
                                }
                              }}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-purple-100 file:text-purple-700"
                            />
                            {(selectedExerciseForEdit.urlVideoSubido || editExerciseVideoFile) && (
                              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                {editExerciseVideoFile ? (
                                  <p className="text-xs text-purple-700">
                                    Nuevo archivo: <span className="font-medium">{editExerciseVideoFile.name}</span>
                                  </p>
                                ) : (
                                  <a
                                    href={selectedExerciseForEdit.urlVideoSubido}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-purple-700 hover:underline break-all"
                                  >
                                    Video actual: {selectedExerciseForEdit.urlVideoSubido}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseEditExerciseModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveEditExercise}
                  disabled={!selectedExerciseForEdit.nombre || !selectedExerciseForEdit.categoria || !selectedExerciseForEdit.dificultad}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Exercise Modal - Update to include new fields */}
      {isNewExerciseModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseNewExerciseModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Nuevo Ejercicio</h3>
                  <p className="text-sm text-gray-500 mt-1">Agrega un nuevo ejercicio a la biblioteca</p>
                </div>
                <button 
                  onClick={handleCloseNewExerciseModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
                <div className="space-y-5">
                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ejercicio <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newExercise.nombre}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej: Press de Banca"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={newExercise.descripcion}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Describe brevemente el ejercicio..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categoría <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newExercise.categoria}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, categoria: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Equipo Necesario
                      </label>
                      <select
                        value={newExercise.equipo}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, equipo: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        {equipos.map(eq => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nivel de Dificultad <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      {dificultades.map(dif => (
                        <button
                          key={dif}
                          type="button"
                          onClick={() => setNewExercise(prev => ({ ...prev, dificultad: dif }))}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            newExercise.dificultad === dif
                              ? dif === 'Principiante' 
                                ? 'bg-green-600 text-white' 
                                : dif === 'Intermedio' 
                                  ? 'bg-orange-500 text-white' 
                                  : 'bg-red-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Músculos Principales
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {musculosDisponibles.map(musculo => (
                        <button
                          key={musculo}
                          type="button"
                          onClick={() => handleNewExerciseMusculoToggle(musculo)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            newExercise.musculosPrincipales.includes(musculo)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {musculo}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Recursos Multimedia
                    </label>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneVideo}
                            disabled={!newExercise.tieneVideo && (newExercise.tieneAnimacion || newExercise.tieneVideoSubido)}
                            onChange={(e) => handleToggleNewExerciseMedia('video', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="ri-video-line text-blue-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Explicativo</span>
                          </div>
                        </label>
                        {newExercise.tieneVideo && (
                          <input
                            type="url"
                            value={newExercise.urlVideo || ''}
                            onChange={(e) => setNewExercise(prev => ({ ...prev, urlVideo: e.target.value }))}
                            placeholder="https://youtube.com/watch?v=..."
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneAnimacion}
                            disabled={!newExercise.tieneAnimacion && (newExercise.tieneVideo || newExercise.tieneVideoSubido)}
                            onChange={(e) => handleToggleNewExerciseMedia('gif', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <i className="ri-play-circle-line text-green-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Animación GIF</span>
                          </div>
                        </label>
                        {newExercise.tieneAnimacion && (
                          <input
                            type="url"
                            value={newExercise.urlGif || ''}
                            onChange={(e) => setNewExercise(prev => ({ ...prev, urlGif: e.target.value }))}
                            placeholder="https://ejemplo.com/animacion.gif"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-3 mb-2">
                          <input
                            type="checkbox"
                            checked={newExercise.tieneVideoSubido}
                            disabled={!newExercise.tieneVideoSubido && (newExercise.tieneVideo || newExercise.tieneAnimacion)}
                            onChange={(e) => handleToggleNewExerciseMedia('upload', e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                              <i className="ri-film-line text-purple-600"></i>
                            </div>
                            <span className="text-sm font-medium text-gray-700">Video Subido (máx. 10 MB)</span>
                          </div>
                        </label>
                        {newExercise.tieneVideoSubido && (
                          <div className="space-y-2">
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/ogg"
                              onChange={async (e) => {
                                const file = e.currentTarget.files?.[0] || null;
                                try {
                                  await handleNewExerciseVideoFileChange(file);
                                } catch (err: any) {
                                  alert(err?.message || 'No se pudo procesar el video.');
                                  e.currentTarget.value = '';
                                }
                              }}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-purple-100 file:text-purple-700"
                            />
                            {(newExerciseVideoFile || newExercise.urlVideoSubido) && (
                              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                {newExerciseVideoFile ? (
                                  <p className="text-xs text-purple-700">
                                    Archivo: <span className="font-medium">{newExerciseVideoFile.name}</span>
                                  </p>
                                ) : (
                                  <p className="text-xs text-purple-700 break-all">Video: {newExercise.urlVideoSubido}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={handleCloseNewExerciseModal}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveExercise}
                  disabled={!newExercise.nombre || !newExercise.categoria || !newExercise.dificultad}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  Guardar Ejercicio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update New Rutina Modal - Add peso field */}
      {isNewRutinaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseNewRutinaModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{isTemplateMode ? 'Nueva Plantilla' : 'Nueva Rutina'}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {isTemplateMode
                      ? 'Crea una plantilla reutilizable. Puedes marcarla como global para todos los usuarios.'
                      : 'Crea una rutina con múltiples sesiones de entrenamiento'}
                  </p>
                </div>
                <button 
                  onClick={handleCloseNewRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="px-6 py-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newRutina.nombre}
                        onChange={(e) => setNewRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={newRutina.descripcion}
                        onChange={(e) => setNewRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setNewRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              newRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={newRutina.duracion}
                          onChange={(e) => setNewRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={newRutina.activa}
                            onChange={(e) => setNewRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    {isTemplateMode && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={templateGlobalScope}
                            onChange={(e) => setTemplateGlobalScope(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Disponible para todos (global)</p>
                            <p className="text-xs text-blue-700">
                              Si está desmarcado, la plantilla queda interna para el equipo administrador.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({sesiones.length})
                        </label>
                        <button
                          onClick={handleAddSesion}
                          disabled={sesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {sesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {sesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-blue-600 mt-0.5"></i>
                        <div className="text-xs text-blue-700">
                          <p className="font-medium mb-1">Sesiones de entrenamiento:</p>
                          <p className="text-xs text-blue-700 mb-2">
                            Esta rutina tendrá {sesiones.length} sesión{sesiones.length !== 1 ? 'es' : ''} que se alternarán durante la semana.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {sesiones.map(sesion => (
                              <span key={sesion.id} className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
                                {sesion.nombre} ({sesion.ejercicios.length} ejercicio{sesion.ejercicios.length !== 1 ? 's' : ''})
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Session Exercises with peso field */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {sesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            sesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {sesionActual && (
                      <>
                        {sesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {sesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEjercicio(index, 'down')}
                                      disabled={index === sesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      min={0}
                                      max={500}
                                      step={0.5}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={ejercicioSearchTerm}
                          onChange={(e) => setEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={categoriaFiltro}
                        onChange={(e) => setCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponibles.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponibles.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="p-3 bg-teal-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-teal-600 mt-0.5"></i>
                        <div className="text-xs text-teal-700">
                          <p className="font-medium mb-1">Consejos:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Incluye calentamiento al inicio</li>
                            <li>Alterna grupos musculares</li>
                            <li>Ajusta según el nivel</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {sesiones.length} sesión{sesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseNewRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveRutina}
                    disabled={!newRutina.nombre || !newRutina.nivel || sesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    {isTemplateMode ? 'Guardar Plantilla' : 'Guardar Rutina'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legacy duplicated modal disabled: keep single source of truth above */}
      {false && isEditRutinaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseEditRutinaModal}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Editar Rutina</h3>
                  <p className="text-sm text-gray-500 mt-1">Modifica la información y ejercicios de la rutina</p>
                </div>
                <button 
                  onClick={handleCloseEditRutinaModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Rutina Info */}
                  <div className="space-y-5">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-file-list-3-line text-teal-600"></i>
                      Información de la Rutina
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Rutina <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editRutina.nombre}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: Rutina Full Body Principiante"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={editRutina.descripcion}
                        onChange={(e) => setEditRutina(prev => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Describe el objetivo y características de la rutina..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel de Dificultad <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3">
                        {dificultades.map(dif => (
                          <button
                            key={dif}
                            type="button"
                            onClick={() => setEditRutina(prev => ({ ...prev, nivel: dif }))}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              editRutina.nivel === dif
                                ? dif === 'Principiante' 
                                  ? 'bg-green-600 text-white' 
                                  : dif === 'Intermedio' 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dif}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración por Sesión (min)
                        </label>
                        <input
                          type="number"
                          value={editRutina.duracion}
                          onChange={(e) => setEditRutina(prev => ({ ...prev, duracion: parseInt(e.target.value) || 0 }))}
                          min={15}
                          max={180}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estado
                        </label>
                        <label className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editRutina.activa}
                            onChange={(e) => setEditRutina(prev => ({ ...prev, activa: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Activa</span>
                        </label>
                      </div>
                    </div>

                    {/* Sesiones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Sesiones ({editSesiones.length})
                        </label>
                        <button
                          onClick={handleAddEditSesion}
                          disabled={editSesiones.length >= 7}
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Agregar
                        </button>
                      </div>
                      <div className="space-y-2">
                        {editSesiones.map((sesion) => (
                          <div key={sesion.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="w-8 h-8 bg-teal-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
                              {sesion.id}
                            </span>
                            <input
                              type="text"
                              value={sesion.nombre}
                              onChange={(e) => handleRenameEditSesion(sesion.id, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {sesion.ejercicios.length} ej.
                            </span>
                            {editSesiones.length > 1 && (
                              <button
                                onClick={() => handleRemoveEditSesion(sesion.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Session Exercises */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-list-check text-teal-600"></i>
                        Ejercicios de la Sesión
                      </h4>
                    </div>

                    {/* Session Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {editSesiones.map((sesion) => (
                        <button
                          key={sesion.id}
                          onClick={() => setEditSesionActiva(sesion.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            editSesionActiva === sesion.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sesion.nombre}
                          <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                            {sesion.ejercicios.length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Ejercicios de la sesión activa */}
                    {editSesionActual && (
                      <>
                        {editSesionActual.ejercicios.length === 0 ? (
                          <div className="p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                            <i className="ri-list-check text-4xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">Agrega ejercicios desde la biblioteca</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {editSesionActual.ejercicios.map((ej, index) => (
                              <div key={ej.ejercicioId} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-teal-600 text-white rounded-full text-xs flex items-center justify-center font-medium">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{ej.nombre}</span>
                                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{ej.categoria}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'up')}
                                      disabled={index === 0}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-up-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleMoveEditEjercicio(index, 'down')}
                                      disabled={index === editSesionActual.ejercicios.length - 1}
                                      className="p-1 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                                    </button>
                                    <button
                                      onClick={() => handleRemoveEjercicioFromEditRutina(ej.ejercicioId)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors cursor-pointer ml-1"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <label className="text-xs text-gray-500">Series</label>
                                    <input
                                      type="number"
                                      value={ej.series}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'series', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={10}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reps</label>
                                    <input
                                      type="number"
                                      value={ej.repeticiones}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'repeticiones', parseInt(e.target.value) || 1)}
                                      min={1}
                                      max={50}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Peso (kg)</label>
                                    <input
                                      type="number"
                                      value={ej.peso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'peso', parseFloat(e.target.value) || 0)}
                                      min={0}
                                      max={500}
                                      step={0.5}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Desc (s)</label>
                                    <input
                                      type="number"
                                      value={ej.descanso}
                                      onChange={(e) => handleUpdateEjercicioEnEditRutina(ej.ejercicioId, 'descanso', parseInt(e.target.value) || 0)}
                                      min={0}
                                      max={300}
                                      step={15}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Column - Exercise Library */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <i className="ri-book-2-line text-teal-600"></i>
                      Biblioteca de Ejercicios
                    </h4>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          placeholder="Buscar ejercicio..."
                          value={editEjercicioSearchTerm}
                          onChange={(e) => setEditEjercicioSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <select
                        value={editCategoriaFiltro}
                        onChange={(e) => setEditCategoriaFiltro(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        {ejerciciosDisponiblesEdit.length === 0 ? (
                          <div className="p-6 text-center">
                            <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                            <p className="text-sm text-gray-500">No hay ejercicios disponibles</p>
                          </div>
                        ) : (
                          ejerciciosDisponiblesEdit.map((ejercicio) => (
                            <div 
                              key={ejercicio.id} 
                              className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{ejercicio.nombre}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ejercicio.dificultad === 'Principiante' ? 'bg-green-100 text-green-700' :
                                      ejercicio.dificultad === 'Intermedio' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {ejercicio.dificultad}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded">{ejercicio.categoria}</span>
                                    <span>{ejercicio.equipo}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddEjercicioToEditRutina(ejercicio)}
                                  className="ml-3 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="text-sm text-gray-500">
                  {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i>
                      {editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0)} ejercicios en {editSesiones.length} sesión{editSesiones.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleCloseEditRutinaModal}
                    className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveEditRutina}
                    disabled={!editRutina.nombre || !editRutina.nivel || editSesiones.reduce((sum, s) => sum + s.ejercicios.length, 0) === 0}
                    className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
